import { useMemo, useEffect, useState, LinkHTMLAttributes } from "react";
import { Helmet } from "react-helmet";
import { ThemingData } from "types/Theming";
import BORDERS_ROUNDED_STYLES from "Theme/EVENTS_CSS_VARS/vars-With-Border-Radius-Default.json";
import BORDERS_SHARP_STYLES from "Theme/EVENTS_CSS_VARS/vars-No-Border-Radius.json";
import googleFonts from "constants/googleFonts";
import hostedFonts from "constants/hostedFonts";

interface EventThemingHeadProps {
  theming: ThemingData;
  onResourcesLoaded?: () => void;
}

type StyleNode = { kind: 'style'; id?: string; css: string };
type LinkNode = { kind: 'link'; rel: string; href: string; as?: string; type?: string; crossOrigin?: LinkHTMLAttributes<HTMLLinkElement>["crossOrigin"] };
type HeadNode = StyleNode | LinkNode;

const buildFontNodes = (fontFamily: string): HeadNode[] => {
  const hostedFont = hostedFonts[fontFamily];
  const nodes: HeadNode[] = [];

  if (hostedFont) {
    const fontFaceRules = Object.entries(hostedFont.weights)
      .map(
        ([weight, url]) =>
          `@font-face { font-display: swap; font-family: ${hostedFont.fontFamily}; font-style: normal; font-weight: ${weight}; src: url('${url}') format('woff2'); }`
      )
      .join('');

    nodes.push({
      kind: 'style',
      id: 'event-font-styles',
      css: `${fontFaceRules} :root { --font-family: ${hostedFont.fontFamily} !important; }`,
    });

    Object.values(hostedFont.weights).forEach((url) => {
      nodes.push({ kind: 'link', rel: 'preload', href: url as string, as: 'font', type: 'font/woff2', crossOrigin: 'anonymous' });
    });
  } else {
    const googleFont = (googleFonts as Record<string, any>)[fontFamily] || (googleFonts as Record<string, any>)['Roboto'];
    if (googleFont?.link) {
      nodes.push({ kind: 'link', rel: 'stylesheet', href: googleFont.link });
    }
    const fontFamilyValue = googleFont?.fontFamily || `'${fontFamily}', sans-serif`;
    nodes.push({ kind: 'style', id: 'event-font-styles', css: `:root { --font-family: ${fontFamilyValue} !important; }` });
  }

  return nodes;
};

export const EventThemingHead = ({ theming, onResourcesLoaded }: EventThemingHeadProps) => {
  const stylesheetUrls = useMemo(() => {
    const urls: string[] = [];
    if (theming.custom_css_file_url) urls.push(theming.custom_css_file_url);
    if (theming.lightThemeCSSFileURL) urls.push(theming.lightThemeCSSFileURL);
    if (theming.darkThemeCSSFileURL) urls.push(theming.darkThemeCSSFileURL);
    return urls;
  }, [theming.custom_css_file_url, theming.lightThemeCSSFileURL, theming.darkThemeCSSFileURL]);

  const [headNodes, setHeadNodes] = useState<HeadNode[]>([]);

  useEffect(() => {
    const nodes: HeadNode[] = [];

    if (theming.borders_rounded) {
      const borderStyles = theming.borders_rounded ? BORDERS_ROUNDED_STYLES : BORDERS_SHARP_STYLES;
      const cssRules = Object.entries(borderStyles)
        .map(([key, value]) => `${key}: ${value} !important;`)
        .join('');
      nodes.push({ kind: 'style', css: `:root { ${cssRules} }` });
    }

    if (theming.font_family) {
      nodes.push(...buildFontNodes(theming.font_family));
    }

    setHeadNodes(nodes);
  }, [theming]);

  const handleHelmetChange = (_newState: any, addedTags: any) => {
    let loadedCount = 0;
    // !!Update this logic
    const totalResources =
      (addedTags.scriptTags?.length || 0) +
      (addedTags.linkTags?.filter((link: HTMLLinkElement) => link.rel === 'stylesheet' && !link.href.includes("fonts.googleapis.com")).length || 0) +
      (addedTags.styleTags?.length || 0);

    const checkAllLoaded = () => {
      loadedCount++;
      if (loadedCount === totalResources) onResourcesLoaded?.();
    };

    if (totalResources === 0) {
      onResourcesLoaded?.();
      return;
    }

    addedTags.scriptTags?.forEach((tag: HTMLScriptElement) => {
      tag.onload = checkAllLoaded;
      tag.onerror = checkAllLoaded;
    });

    addedTags.linkTags
      ?.filter((link: HTMLLinkElement) => link.rel === 'stylesheet')
      .forEach((tag: HTMLLinkElement) => {
        tag.onload = checkAllLoaded;
        tag.onerror = checkAllLoaded;
      });

    addedTags.styleTags?.forEach((tag: HTMLStyleElement) => {
      tag.onload = checkAllLoaded;
      tag.onerror = checkAllLoaded;
    });
  };
  return (
    <Helmet onChangeClientState={handleHelmetChange}>
      {stylesheetUrls.map((url) => (
        <link key={url} rel="stylesheet" href={url} />
      ))}

      {headNodes.map((node, i) =>
        node.kind === 'style' ? (
          <style key={node.id ?? i} id={node.id} >
            {node.css || ""}
          </style>
        ) : (
          <link key={`${node.rel}-${node.href}`} rel={node.rel} href={node.href} as={node.as} type={node.type} crossOrigin={node.crossOrigin || "anonymous"} />
        )
      )}

      {theming.custom_js_file_url && (
        <script src={theming.custom_js_file_url} crossOrigin="anonymous" />
      )}

      {theming.favicon && <link rel="icon" type="image/x-icon" href={theming.favicon} />}
    </Helmet>
  );
};
